/**
 * Terms of Service Screen
 * Conditions Generales d'Utilisation
 * URL: https://wealfi.co/fr/terms
 */

import React from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import useDomain from '../../hooks/useDomain';
import useWebSEO from '../../hooks/useWebSEO';

// Wealfi Brand Colors
const brandColors = {
  green: '#71B352',
  greenDark: '#5B8A3C',
  greenLight: '#E8F5E0',
  dark: '#3D4543',
  orange: '#D97234',
  orangeLight: '#FFF3EB',
  gray: '#6B7280',
  background: '#FAFBFC',
  white: '#FFFFFF',
};

export default function TermsOfServiceScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { isLandingDomain } = useDomain();

  useWebSEO({
    title: 'Conditions Générales d\'Utilisation — Wealfi',
    description: 'Conditions générales d\'utilisation de Wealfi. Abonnements, avertissement financier, connexion bancaire Powens, droits et obligations.',
    keywords: 'CGU Wealfi, conditions utilisation, mentions légales fintech',
    ogUrl: 'https://wealfi.co/fr/terms',
  });

  const handleBack = () => {
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = '/fr';
      return;
    }
    navigation.navigate('Landing' as any);
  };

  return (
    <View style={styles.container}>
      {/* Fixed Navbar */}
      <View style={[styles.navbar, isDesktop && styles.navbarDesktop]}>
        <Pressable onPress={handleBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Retour</Text>
        </Pressable>
        <View style={styles.logoContainer}>
          <Text style={styles.logoText}>
            Wealfi
          </Text>
        </View>
        <View style={{ width: 80 }} />
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.content, { maxWidth: isDesktop ? 800 : '100%' }]}>
          <Text style={styles.pageTitle}>Conditions Générales d’Utilisation</Text>
          <Text style={styles.updateDate}>Dernière mise à jour : 24 février 2026</Text>

          {/* Article 1 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 1 — Objet et acceptation</Text>
            <Text style={styles.paragraph}>
              Les présentes Conditions Générales d’Utilisation (« CGU ») régissent l’accès et l’utilisation du service Wealfi, accessible via l’application web et mobile. En créant un compte ou en utilisant le Service, tu acceptes sans réserve les présentes CGU. Si tu n’acceptes pas ces conditions, tu ne dois pas utiliser le Service.
            </Text>
          </View>

          {/* Article 2 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 2 — Définitions</Text>
            <Text style={styles.bulletItem}>
              • « Service » : l’ensemble des fonctionnalités proposées par Wealfi (diagnostic financier, assistant IA, suivi d’objectifs, éducation financière, connexion bancaire optionnelle).
            </Text>
            <Text style={styles.bulletItem}>
              • « Utilisateur » ou « tu » : toute personne physique inscrite et utilisant le Service.
            </Text>
            <Text style={styles.bulletItem}>
              • « Contenu IA » : tout texte, analyse ou recommandation générée par l’intelligence artificielle du Service.
            </Text>
            <Text style={styles.bulletItem}>
              • « Données Financières » : les informations financières que tu déclares manuellement ou qui sont récupérées via la connexion bancaire.
            </Text>
            <Text style={styles.bulletItem}>
              • « Connexion Bancaire » : la fonctionnalité optionnelle permettant de synchroniser tes comptes bancaires via notre partenaire Powens.
            </Text>
          </View>

          {/* Article 3 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 3 — Inscription et compte utilisateur</Text>
            <Text style={styles.paragraph}>
              Pour utiliser le Service, tu dois créer un compte en fournissant une adresse email valide. Tu es responsable de la confidentialité de tes identifiants de connexion. Toute activité réalisée depuis ton compte est réputée effectuée par toi. Tu t’engages à nous informer immédiatement en cas d’utilisation non autorisée de ton compte à l’adresse contact@wealfi.co.
            </Text>
          </View>

          {/* Article 4 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 4 — Description des services</Text>
            <Text style={styles.paragraph}>
              Wealfi propose les fonctionnalités suivantes :
            </Text>
            <Text style={styles.bulletItem}>
              • Diagnostic financier personnalisé par IA : analyse de ta situation financière et identification d’optimisations possibles
            </Text>
            <Text style={styles.bulletItem}>
              • Assistant IA disponible 24h/24 et 7j/7 : pour répondre à tes questions financières du quotidien
            </Text>
            <Text style={styles.bulletItem}>
              • Objectifs SMART : définition et suivi d’objectifs financiers personnalisés
            </Text>
            <Text style={styles.bulletItem}>
              • Éducation financière : contenus pédagogiques adaptés à ton profil
            </Text>
            <Text style={styles.bulletItem}>
              • Connexion bancaire optionnelle : synchronisation de tes comptes via Powens (établissement de paiement agréé ACPR n°16948) pour un diagnostic plus précis
            </Text>
          </View>

          {/* Article 5 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 5 — Abonnements et tarifs</Text>
            <Text style={styles.paragraph}>
              Le Service est proposé selon les formules suivantes :
            </Text>
            <Text style={styles.bulletItem}>
              • Gratuit : accès limité au diagnostic et à l’assistant IA (5 messages/jour)
            </Text>
            <Text style={styles.bulletItem}>
              • Premium : 9,99€/mois — accès illimité à toutes les fonctionnalités, connexion bancaire incluse
            </Text>
            <Text style={styles.bulletItem}>
              • Pro : tarif à venir — fonctionnalités avancées pour les professionnels
            </Text>
            <Text style={styles.paragraph}>
              Les tarifs sont indiqués en euros TTC. Wealfi se réserve le droit de modifier ses tarifs à tout moment, les modifications prenant effet au prochain renouvellement.
            </Text>
          </View>

          {/* Article 6 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 6 — Paiement et renouvellement</Text>
            <Text style={styles.paragraph}>
              Le paiement est géré de manière sécurisée par notre prestataire Stripe. L’abonnement Premium est renouvelé automatiquement à chaque échéance mensuelle, conformément aux dispositions de l’article L215-1 du Code de la consommation. Tu seras informé(e) de tout renouvellement à venir. Tu peux annuler ton abonnement à tout moment depuis les paramètres de ton compte. L’annulation prend effet à la fin de la période en cours.
            </Text>
          </View>

          {/* Article 7 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 7 — Droit de rétractation</Text>
            <Text style={styles.paragraph}>
              Conformément à l’article L221-18 du Code de la consommation, tu disposes d’un délai de 14 jours à compter de la souscription pour exercer ton droit de rétractation, sans avoir à justifier de motif. Pour exercer ce droit, contacte-nous à contact@wealfi.co. En cas d’utilisation du Service pendant le délai de rétractation, le montant correspondant à l’utilisation effective pourra être retenu proportionnellement.
            </Text>
          </View>

          {/* Article 8 — Highlighted Warning */}
          <View style={[styles.section, styles.warningSection]}>
            <Text style={styles.heading}>Article 8 — Avertissement financier</Text>
            <Text style={styles.warningLabel}>IMPORTANT</Text>
            <Text style={styles.paragraph}>
              Wealfi n’est PAS :
            </Text>
            <Text style={styles.bulletItem}>
              • Un Conseiller en Investissements Financiers (CIF) enregistré auprès de l’AMF
            </Text>
            <Text style={styles.bulletItem}>
              • Un Conseiller en Gestion de Patrimoine (CGP)
            </Text>
            <Text style={styles.bulletItem}>
              • Un Prestataire de Services d’Investissement (PSI)
            </Text>
            <Text style={styles.bulletItem}>
              • Un établissement de crédit ou de paiement
            </Text>
            <Text style={styles.paragraph}>
              Les informations et analyses fournies par Wealfi sont à caractère éducatif et informatif uniquement. Elles ne constituent en aucun cas un conseil en investissement, une recommandation personnalisée ou une incitation à réaliser des opérations financières au sens de la réglementation de l’Autorité des Marchés Financiers (AMF).
            </Text>
            <Text style={styles.paragraph}>
              Tu restes seul(e) décisionnaire de tes choix financiers. Wealfi ne garantit aucun résultat financier. Avant toute décision d’investissement, nous te recommandons de consulter un professionnel agréé.
            </Text>
          </View>

          {/* Article 9 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 9 — Contenus générés par IA</Text>
            <Text style={styles.paragraph}>
              Les contenus générés par l’intelligence artificielle de Wealfi sont produits de manière automatisée et peuvent contenir des erreurs ou des imprécisions. Ces contenus :
            </Text>
            <Text style={styles.bulletItem}>
              • Ne constituent pas des recommandations personnalisées certifiées
            </Text>
            <Text style={styles.bulletItem}>
              • Peuvent ne pas tenir compte de l’intégralité de ta situation personnelle
            </Text>
            <Text style={styles.bulletItem}>
              • Sont fournis à titre informatif et éducatif
            </Text>
            <Text style={styles.bulletItem}>
              • Doivent être vérifiés avant toute prise de décision
            </Text>
            <Text style={styles.paragraph}>
              Wealfi s’efforce d’améliorer continuellement la qualité de ses analyses mais ne peut garantir leur exactitude à tout moment.
            </Text>
          </View>

          {/* Article 10 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 10 — Connexion bancaire</Text>
            <Text style={styles.paragraph}>
              La connexion bancaire est une fonctionnalité optionnelle assurée par Powens SAS, établissement de paiement agréé par l’Autorité de Contrôle Prudentiel et de Résolution (ACPR) sous le numéro 16948.
            </Text>
            <Text style={styles.bulletItem}>
              • Wealfi ne stocke pas tes identifiants bancaires
            </Text>
            <Text style={styles.bulletItem}>
              • La connexion est initiée et sécurisée par Powens
            </Text>
            <Text style={styles.bulletItem}>
              • Tu peux révoquer l’accès à tout moment depuis les paramètres de ton compte
            </Text>
            <Text style={styles.bulletItem}>
              • Les données bancaires récupérées sont utilisées uniquement pour enrichir ton diagnostic financier
            </Text>
          </View>

          {/* Article 11 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 11 — Propriété intellectuelle</Text>
            <Text style={styles.paragraph}>
              L’ensemble des éléments du Service (textes, graphiques, logiciels, bases de données, marques, logos) sont la propriété exclusive de Wealfi ou de ses partenaires. Toute reproduction, représentation ou exploitation non autorisée est interdite et constituerait une contrefaçon sanctionnée par le Code de la propriété intellectuelle.
            </Text>
          </View>

          {/* Article 12 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 12 — Obligations de l’utilisateur</Text>
            <Text style={styles.paragraph}>
              En utilisant le Service, tu t’engages à :
            </Text>
            <Text style={styles.bulletItem}>
              • Fournir des informations exactes et à jour
            </Text>
            <Text style={styles.bulletItem}>
              • Ne pas utiliser le Service à des fins illicites ou frauduleuses
            </Text>
            <Text style={styles.bulletItem}>
              • Ne pas tenter de contourner les mesures de sécurité du Service
            </Text>
            <Text style={styles.bulletItem}>
              • Ne pas reproduire ou extraire le contenu du Service de manière automatisée
            </Text>
            <Text style={styles.bulletItem}>
              • Respecter les droits de propriété intellectuelle de Wealfi
            </Text>
          </View>

          {/* Article 13 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 13 — Responsabilité et limitations</Text>
            <Text style={styles.paragraph}>
              Wealfi s’engage à fournir le Service avec diligence. Toutefois, Wealfi ne peut être tenu responsable :
            </Text>
            <Text style={styles.bulletItem}>
              • Des décisions financières prises sur la base des contenus du Service
            </Text>
            <Text style={styles.bulletItem}>
              • Des pertes financières directes ou indirectes liées à l’utilisation du Service
            </Text>
            <Text style={styles.bulletItem}>
              • Des interruptions temporaires du Service pour maintenance ou mise à jour
            </Text>
            <Text style={styles.bulletItem}>
              • Des erreurs ou imprécisions dans les contenus générés par l’IA
            </Text>
            <Text style={styles.bulletItem}>
              • De l’indisponibilité temporaire des services tiers (Powens, Stripe)
            </Text>
            <Text style={styles.paragraph}>
              La responsabilité de Wealfi est limitée au montant des sommes versées par l’Utilisateur au cours des 12 derniers mois.
            </Text>
          </View>

          {/* Article 14 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 14 — Résiliation et suppression de compte</Text>
            <Text style={styles.paragraph}>
              Tu peux résilier ton compte à tout moment :
            </Text>
            <Text style={styles.bulletItem}>
              • Depuis les paramètres de l’application (suppression de compte)
            </Text>
            <Text style={styles.bulletItem}>
              • En envoyant une demande à contact@wealfi.co
            </Text>
            <Text style={styles.paragraph}>
              La suppression de ton compte entraîne la suppression de tes données personnelles, sous réserve des obligations légales de conservation. En cas de violation des présentes CGU, Wealfi se réserve le droit de suspendre ou supprimer ton compte sans préavis.
            </Text>
          </View>

          {/* Article 15 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 15 — Modification des CGU</Text>
            <Text style={styles.paragraph}>
              Wealfi se réserve le droit de modifier les présentes CGU à tout moment. Tu seras informé(e) de toute modification substantielle par email ou par notification dans l’application. La poursuite de l’utilisation du Service après notification vaut acceptation des nouvelles CGU.
            </Text>
          </View>

          {/* Article 16 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 16 — Médiation de la consommation</Text>
            <Text style={styles.paragraph}>
              Conformément aux articles L611-1 et suivants du Code de la consommation, en cas de litige non résolu, tu peux recourir gratuitement à un médiateur de la consommation. Le médiateur désigné sera communiqué ultérieurement. En attendant, tu peux nous contacter à contact@wealfi.co pour toute réclamation.
            </Text>
          </View>

          {/* Article 17 */}
          <View style={styles.section}>
            <Text style={styles.heading}>Article 17 — Droit applicable et juridiction</Text>
            <Text style={styles.paragraph}>
              Les présentes CGU sont soumises au droit français. En cas de litige, les parties s’efforceront de trouver une solution amiable. À défaut, les tribunaux compétents de Paris seront seuls compétents.
            </Text>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Pressable onPress={handleBack}>
              <Text style={styles.footerLink}>← Retour à l’accueil</Text>
            </Pressable>
            <Text style={styles.footerCopyright}>© 2026 Wealfi. Tous droits réservés.</Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: brandColors.white,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 70,
    paddingBottom: 60,
    alignItems: 'center',
  },
  content: {
    width: '100%',
    paddingHorizontal: 24,
  },
  navbar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  navbarDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 16,
  },
  backButton: {
    width: 80,
  },
  backButtonText: {
    fontSize: 16,
    color: brandColors.green,
    fontWeight: '600',
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoText: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
  },
  logoTextAccent: {
    color: brandColors.green,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
    marginTop: 20,
  },
  updateDate: {
    fontSize: 14,
    color: brandColors.gray,
    marginBottom: 32,
  },
  section: {
    marginBottom: 32,
  },
  heading: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 12,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 26,
    color: '#374151',
    marginBottom: 8,
  },
  bulletItem: {
    fontSize: 16,
    lineHeight: 26,
    color: '#374151',
    paddingLeft: 16,
    marginBottom: 4,
  },
  warningSection: {
    backgroundColor: brandColors.orangeLight,
    padding: 20,
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: brandColors.orange,
  },
  warningLabel: {
    fontWeight: '700',
    color: brandColors.orange,
    fontSize: 14,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  footer: {
    marginTop: 40,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    alignItems: 'center',
    paddingBottom: 40,
  },
  footerLink: {
    fontSize: 16,
    color: brandColors.green,
    fontWeight: '600',
    marginBottom: 12,
  },
  footerCopyright: {
    fontSize: 14,
    color: brandColors.gray,
  },
});
